import type { FC } from "react";
import { useMemo } from "react";
import type { SourceUrlUIPart } from "ai";
import styled from "@emotion/styled";
import { colors, spacing } from "../design-tokens";
import { LinkText, ParagraphRegular } from "../primitives/typographies";
import { getHostname } from "@/utils/url-helpers";

const SourcesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg}px;
  width: 100%;
`;

const SourceItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs}px;
  padding-bottom: ${spacing.lg}px;
  border-bottom: 1px solid ${colors.border};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const SourceTitle = styled(LinkText)`
  display: block;
  font-weight: 500;
  margin: 0;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SourceUrl = styled(ParagraphRegular)`
  color: ${colors.neutralGrey};
  margin: 0;
  font-size: 14px;
`;

type SourcesListProps = {
  sources: SourceUrlUIPart[];
};

export const SourcesList: FC<SourcesListProps> = ({ sources }) => {
  // Memoize hostname extraction to avoid repeated URL parsing
  const sourcesWithHostnames = useMemo(
    () =>
      sources.map(source => ({
        ...source,
        hostname: getHostname(source.url) || source.url,
      })),
    [sources],
  );

  return (
    <SourcesContainer>
      {sourcesWithHostnames.map((source, index) => (
        <SourceItem key={source.sourceId || index}>
          <SourceTitle
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {source.title}
          </SourceTitle>
          <SourceUrl>{source.hostname}</SourceUrl>
        </SourceItem>
      ))}
    </SourcesContainer>
  );
};
